import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaAward, FaUsers, FaFlask, FaHeart } from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const stats = [
    { icon: FaAward, number: '15+', label: 'Years Experience' },
    { icon: FaUsers, number: '1000+', label: 'Satisfied Customers' },
    { icon: FaFlask, number: '11', label: 'Quality Products' },
    { icon: FaHeart, number: '24/7', label: 'Care & Support' }
  ];

  const featuredProducts = [
    {
      name: 'LINREGA-NT',
      category: 'Neuropathic Pain',
      description: 'Pregabalin 75mg + Nortriptyline 10mg Tablet for neuropathic pain management',
      color: '#E67E22'
    },
    {
      name: 'EASEZOLE 40MG',
      category: 'PPIs',
      description: 'Esomeprazole 40mg Tablet for acid-related disorders',
      color: '#3498DB'
    },
    {
      name: 'NEUFOBEN PLUS',
      category: 'Vitamins & Neuropathy',
      description: 'Alphalipoicacid + Methylcobalamin + Vitamin B6 + Folic acid + Benfothiamine + Biotin + Chromium Capsules',
      color: '#C0392B'
    }
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <motion.div
              className="hero-text"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="hero-title">
                <span className="title-main">EMPOWERING LIVES WITH</span>
                <span className="title-highlight">PRECISION MEDICINE</span>
              </h1>
              <p className="hero-subtitle">Your Health, Our Priority</p>
              <p className="hero-description">
                Launched in 2024, we specialize in Neuro, Psychiatry, and Pain Management medicines, 
                bringing innovation and care to the forefront. Though new to the pharma industry, 
                our team comprises seasoned experts with extensive experience. Partnering with 
                renowned manufacturers who uphold the highest standards, we are committed to 
                delivering safe, effective, and reliable healthcare solutions.
              </p>
              <div className="hero-buttons">
                <Link to="/products" className="btn btn-primary">
                  Explore Products <FaArrowRight />
                </Link>
                <Link to="/about" className="btn btn-secondary">
                  Learn More
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="hero-stats"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="stat-item"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <stat.icon className="stat-icon" />
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview section">
        <div className="container">
          <div className="about-content">
            <motion.div
              className="about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">About Sarvatman Life Science</h2>
              <p className="about-description">
                With over 15 years of combined experience in the pharmaceutical industry, 
                our team brings deep expertise and commitment to excellence. We focus on 
                developing and distributing high-quality medicines in specialized therapeutic 
                areas including Neurology, Psychiatry, and Pain Management.
              </p>
              <p className="about-description">
                Our mission is to improve patient outcomes through innovative pharmaceutical 
                solutions, backed by rigorous quality standards and unwavering dedication 
                to healthcare excellence.
              </p>
              <Link to="/about" className="btn btn-primary">
                Read More <FaArrowRight />
              </Link>
            </motion.div>
            <motion.div
              className="about-features"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="feature-grid">
                <div className="feature-item">
                  <h3>Quality Assurance</h3>
                  <p>Partnering with renowned manufacturers maintaining highest standards</p>
                </div>
                <div className="feature-item">
                  <h3>Expert Team</h3>
                  <p>Seasoned professionals with extensive pharmaceutical experience</p>
                </div>
                <div className="feature-item">
                  <h3>Specialized Focus</h3>
                  <p>Dedicated to Neuro, Psychiatry, and Pain Management solutions</p>
                </div>
                <div className="feature-item">
                  <h3>Innovation</h3>
                  <p>Bringing cutting-edge medical solutions to improve lives</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="products-preview section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Featured Products</h2>
            <p className="section-subtitle">
              Discover our range of specialized pharmaceutical solutions designed 
              to improve patient outcomes and quality of life.
            </p>
          </motion.div>
          
          <div className="products-grid">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={index}
                className="product-card"
                style={{ '--product-color': product.color }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                viewport={{ once: true }}
              >
                <div className="product-header">
                  <span className="product-category">{product.category}</span>
                </div>
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">{product.description}</p>
                <div className="product-footer">
                  <span className="learn-more">Learn More →</span>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            className="products-cta"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link to="/products" className="btn btn-primary">
              View All Products <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
